import { DeepAnalysisResult } from '@/types/analysisTypes';
import { 
  isNewBonusFormat, 
  createStructuredInsightFromString, 
  convertLegacyToNewFormat,
  isNewFormatExpertInsight,
  isNewFormatUnexpectedAnalogy,
  isNewFormatChallengedAssumption,
  isNewFormatInsiderTip
} from '@/utils/bonus-section';

/**
 * Process an analysis result to ensure it has the expected format
 */
export function processAnalysisResult(result: DeepAnalysisResult): DeepAnalysisResult {
  console.log('[DEBUG] processAnalysisResult - ENTRY - category_scores:', Object.keys(result.category_scores || {}));
  
  // Clean rebuild: Clone result and pass category_scores unmodified
  const processedResult = JSON.parse(JSON.stringify(result));
  
  // Only process shareability scores and bonus sections - DO NOT MODIFY category_scores
  processShareabilityScores(processedResult);
  processBonusSection(processedResult);
  
  console.log('[DEBUG] Final category_scores passed to UI:', Object.keys(processedResult.category_scores || {}));
  
  return processedResult;
}

// REMOVED: This function is no longer used in the clean rebuild

/**
 * Process shareability scores to ensure they have the expected format
 */
function processShareabilityScores(result: DeepAnalysisResult): void {
  if (typeof result.shareability_scores === 'object' && 
      result.shareability_scores) {
    // Check if we need to convert number format to object format
    ['beginner', 'intermediate', 'expert'].forEach(audience => {
      const audienceKey = audience as keyof typeof result.shareability_scores;
      const value = result.shareability_scores[audienceKey];
      
      if (typeof value === 'number') {
        result.shareability_scores[audienceKey] = { 
          score: value, 
          explanation: 'Score provided without detailed explanation', 
          improvement: 'Consider requesting more detailed analysis' 
        };
      }
    });
  }
}

/**
 * Process bonus section to ensure it has the expected format
 */
function processBonusSection(result: DeepAnalysisResult): void {
  // Handle creative insights from different formats
  if (!result.bonus_section && result.bonus_creative_insights) {
    // Case 1: We have a string of insights but no bonus_section
    result.bonus_section = createStructuredInsightFromString(result.bonus_creative_insights);
  } else if (result.bonus_section && !isNewBonusFormat(result.bonus_section)) {
    // Case 2: We have a legacy bonus_section format
    result.bonus_section = convertLegacyToNewFormat(result.bonus_section);
  } else if (!result.bonus_section) {
    // Case 3: No bonus section at all - create an empty one
    result.bonus_section = createEmptyBonusSection();
  }

  // Make sure all bonus section items exist and have empty arrays at minimum
  ensureBonusSectionArrays(result);
}

/**
 * Create an empty bonus section with default values
 */
function createEmptyBonusSection() {
  return {
    expert_insight: { 
      title: 'Expert Insight',
      intro: 'No specific insights available for this content',
      key_takeaways: [
        'Content analysis completed successfully',
        'Review other sections for detailed feedback',
        'Consider running a more detailed analysis'
      ],
      conclusion: 'See the overall score and category breakdowns for more information'
    },
    unexpected_analogy: {
      title: 'Unexpected Analogy',
      intro: '',
      key_points: [],
      conclusion: ''
    },
    challenged_assumption: {
      title: 'Challenged Assumption',
      intro: '',
      key_reasons: [],
      solution: ''
    },
    insider_tip: {
      title: 'Insider Tip',
      intro: '',
      step_by_step: [],
      final_takeaway: ''
    }
  };
}

/**
 * Ensure bonus section arrays exist
 */
function ensureBonusSectionArrays(result: DeepAnalysisResult): void {
  if (!result.bonus_section) return;
  
  // Check expert_insight
  if (result.bonus_section.expert_insight) {
    if (isNewFormatExpertInsight(result.bonus_section.expert_insight)) {
      if (!result.bonus_section.expert_insight.key_takeaways) {
        result.bonus_section.expert_insight.key_takeaways = [];
      }
    }
  }
  
  // Check unexpected_analogy
  if (result.bonus_section.unexpected_analogy) {
    if (isNewFormatUnexpectedAnalogy(result.bonus_section.unexpected_analogy)) {
      if (!result.bonus_section.unexpected_analogy.key_points) {
        result.bonus_section.unexpected_analogy.key_points = [];
      }
    }
  }
  
  // Check challenged_assumption
  if (result.bonus_section.challenged_assumption) {
    if (isNewFormatChallengedAssumption(result.bonus_section.challenged_assumption)) {
      if (!result.bonus_section.challenged_assumption.key_reasons) {
        result.bonus_section.challenged_assumption.key_reasons = [];
      }
    }
  }
  
  // Check insider_tip
  if (result.bonus_section.insider_tip) {
    if (isNewFormatInsiderTip(result.bonus_section.insider_tip)) {
      if (!result.bonus_section.insider_tip.step_by_step) {
        result.bonus_section.insider_tip.step_by_step = [];
      }
    }
  }
}
